import { useEffect, useMemo, useState } from "react";
import { Box, Button, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import ChatPanel from "../addressbook/panels/ChatPanel";
import { useRemoteLLM } from "../../contexts/RemoteLLMContext";

/*
 * Open-ended chat against the self-hosted open-source model servers
 * (Kimi-Linear 48B and DeepSeek-R1 32B on llm.prod.oelement.net).
 *
 * Reuses the ONE shared <ChatPanel> in its plain/external-engine mode — no
 * duplicated chat shell, streaming loop, or voice input. The GPU server stops
 * itself when idle; this page surfaces that state and offers a wake action.
 */

const STATUS_TEXT = {
  unknown: "Checking AI server...",
  stopped: "The AI server is asleep. Wake it to start chatting.",
  starting: "Waking the AI server...",
  loading: "AI server is starting and loading models. This can take a few minutes...",
  error: "Cannot reach the AI server.",
};

export default function PageLLMChat() {
  const { serverState, refreshStatus, wake, makeEngine, models } = useRemoteLLM();
  const [modelKey, setModelKey] = useState("kimi");

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const ready = serverState === "ready";
  const llm = useMemo(() => ({
    engine: makeEngine(modelKey),
    ready,
    loading: serverState === "starting" || serverState === "loading" || serverState === "unknown",
    error: serverState === "error" ? STATUS_TEXT.error : null,
    statusText: STATUS_TEXT[serverState] || null,
  }), [makeEngine, modelKey, ready, serverState]);

  const model = models[modelKey];

  const titleExtras = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1 }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={modelKey}
        onChange={(e, v) => { if (v) setModelKey(v); }}
        sx={{ "& .MuiToggleButton-root": { py: 0, px: 1.25, fontSize: 11, textTransform: "none" } }}
      >
        {Object.values(models).map((m) => (
          <ToggleButton key={m.key} value={m.key}>
            <Tooltip title={m.detail}><span>{m.label}</span></Tooltip>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {serverState === "stopped" && (
        <Button size="small" variant="contained" onClick={wake} sx={{ py: 0, fontSize: 11, textTransform: "none" }}>
          Wake server
        </Button>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", height: "100%", minHeight: 0, minWidth: 0, overflow: "hidden", p: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ChatPanel
          plain
          llm={llm}
          title="AI Chat"
          titleExtras={titleExtras}
          maxTokens={4096}
          stripThink
          emptyTitle={`Ask ${model.label} anything`}
          emptySubtitle="Runs on your private AI server."
          inputPlaceholder={`Ask ${model.label}...`}
        />
      </Box>
    </Box>
  );
}
